import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import {
  Zap,
  Shield,
  Briefcase,
  Wrench,
  Truck,
  Award,
  Bot,
  ArrowRight,
  TrendingUp,
} from "lucide-react";

const industrialPipeline = "/images/industrial-pipeline.jpeg";
const oilPlatforms = "/images/oil-platforms.jpeg";

const servicesData = [
  {
    id: 1,
    title: "Engineering Services",
    shortDescription: "Expert engineering design and consultation",
    slug: "engineering",
    icon: Zap,
    color: "from-blue-400 to-cyan-400",
  },
  {
    id: 2,
    title: "Procurement Services",
    shortDescription: "Efficient equipment and materials sourcing",
    slug: "procurement",
    icon: Briefcase,
    color: "from-purple-400 to-pink-400",
  },
  {
    id: 3,
    title: "Construction Services",
    shortDescription: "Full-scale construction and installation",
    slug: "construction",
    icon: Wrench,
    color: "from-orange-400 to-red-400",
  },
  {
    id: 4,
    title: "Installation Services",
    shortDescription: "Professional installation and commissioning",
    slug: "installation",
    icon: Truck,
    color: "from-green-400 to-emerald-400",
  },
  {
    id: 5,
    title: "Equipment Leasing",
    shortDescription: "Flexible equipment solutions for projects",
    slug: "leasing",
    icon: Shield,
    color: "from-indigo-400 to-purple-400",
  },
  {
    id: 6,
    title: "Safety Equipment Supply",
    shortDescription: "Comprehensive workplace safety solutions",
    slug: "safety",
    icon: Award,
    color: "from-yellow-400 to-orange-400",
  },
  {
    id: 7,
    title: "Robotic Inspection Systems",
    shortDescription: "Advanced robotic crawler pipe and asset inspection",
    slug: "robotic-inspection",
    icon: Bot,
    color: "from-cyan-400 to-blue-400",
  },
];

const benefits = [
  "Local expertise with global standards",
  "Strong HSE culture and compliance",
  "Proven track record of successful projects",
  "Competitive pricing and flexible terms",
  "Dedicated project teams",
  "Full lifecycle support",
];

export default function Services() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Engineering Services Nigeria - Alkanes Engineering Limited | Oil & Gas Solutions"
        description="Alkanes Engineering Limited offers comprehensive engineering services in Lagos, Nigeria: Engineering Design, Procurement, Construction, Installation, Equipment Leasing, Safety Equipment, and Robotic Inspection Systems."
        keywords="Alkanes Engineering, engineering services, oil and gas services, construction procurement, equipment leasing Nigeria, engineering company Lagos"
        canonical="https://alkanesengineering.com/services"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/35"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
            Our Services
          </h1>
          <p className="text-lg text-gray-100 max-w-2xl">
            Comprehensive engineering and construction solutions tailored to
            your project requirements.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section
        className="relative py-16 md:py-24"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Specialized Services
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Click on any service to learn more about our comprehensive
              solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group relative rounded-2xl border-2 border-gray-700 p-8 transition-all duration-300 cursor-pointer text-left hover:border-primary hover:shadow-2xl hover:shadow-primary/15 hover:-translate-y-2 bg-gray-800 hover:bg-gray-900"
                >
                  <div
                    className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${service.color} text-white mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Icon size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {service.shortDescription}
                  </p>
                  <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                    Learn More <ArrowRight size={20} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        className="relative py-16 md:py-24"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose Alkane Engineering
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Our commitment to excellence, safety, and innovation sets us
              apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-800 p-6 rounded-xl border-2 border-gray-700 hover:border-primary transition-all group"
              >
                <div className="flex items-start gap-4">
                  <TrendingUp className="w-6 h-6 text-primary flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                  <p className="font-semibold text-white text-lg">{benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative text-white py-16 md:py-24"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/85"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-gray-100 max-w-2xl mx-auto">
            Contact our team to discuss your specific project requirements.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1 active:scale-95"
          >
            Get in Touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
